import {
  AssignmentTypeEnum,
  AssignmentViewModel,
  CompanyModel,
} from 'api/model';

const normalize = (str: string): string => {
  return str.toLowerCase().replace(/\s/g, '');
};

function addressMatches(
  company: CompanyModel,
  name: string,
  address: string,
  zip: string,
  city: string
) {
  return (
    normalize(name) === normalize(company.name) &&
    normalize(address) === normalize(company.address) &&
    normalize(zip) === normalize(company.zip) &&
    normalize(city) === normalize(company.city)
  );
}

export function assignmentAddressesMatches(
  assignments: AssignmentViewModel[],
  fromAddress?: CompanyModel | null,
  toAddress?: CompanyModel | null
) {
  const filteredFrom = fromAddress
    ? assignments.filter((a) =>
        addressMatches(
          fromAddress,
          a.assignment.fromName,
          a.assignment.fromAddress,
          a.assignment.fromZip,
          a.assignment.fromCity
        )
      )
    : assignments;

  const filteredTo = toAddress
    ? filteredFrom.filter((a) =>
        addressMatches(
          toAddress,
          a.assignment.toName,
          a.assignment.toAddress,
          a.assignment.toZip,
          a.assignment.toCity
        )
      )
    : filteredFrom;

  return filteredTo;
}

/** Hubbfiltrets extra alternativ: ben vars frånadress inte hör till någon hubb. */
export const OTHER_HUBS_FILTER_VALUE = 'other' as const;

export type HubFilterValue = number | typeof OTHER_HUBS_FILTER_VALUE;

const addressKey = (address: string, zip: string, city: string) =>
  normalize(address) + normalize(zip) + normalize(city);

/**
 * Hubbarnas companyID per adressnyckel. Flera hubbar kan i teorin dela adress — då hör ett ben
 * som utgår därifrån till alla dem.
 */
const buildHubIdsByAddress = (hubs: CompanyModel[]) => {
  const hubIdsByAddress = new Map<string, number[]>();
  hubs.forEach((hub) => {
    const key = addressKey(hub.address, hub.zip, hub.city);
    hubIdsByAddress.set(key, [
      ...(hubIdsByAddress.get(key) ?? []),
      hub.companyID,
    ]);
  });
  return hubIdsByAddress;
};

/** Hubbarna (eller undefined = "Övriga") som ett ben utgår från, via frånadressen. */
const fromHubIds = (
  assignment: AssignmentViewModel['assignment'],
  hubIdsByAddress: Map<string, number[]>
) =>
  hubIdsByAddress.get(
    addressKey(assignment.fromAddress, assignment.fromZip, assignment.fromCity)
  );

/**
 * Filtrerar mellanlandningsbenen på frånadressens hubb (företag flaggade `isZakraHub`).
 *
 * Matchar på adress + postnummer + ort, inte på namn: namnet på ett ben är fritext och kan
 * skilja sig från företagskortet, men adressen är det som gör platsen till en hubb. Tom
 * selektion = inget filter. Valda hubbar och OTHER_HUBS_FILTER_VALUE ("Övriga") kombineras
 * med ELLER, så alla ben visas om allt är ibockat.
 */
export function assignmentFromHubMatches(
  assignments: AssignmentViewModel[],
  hubs: CompanyModel[],
  selectedHubs: Set<HubFilterValue>
) {
  if (selectedHubs.size === 0) return assignments;

  const hubIdsByAddress = buildHubIdsByAddress(hubs);

  return assignments.filter(({ assignment }) => {
    const hubIds = fromHubIds(assignment, hubIdsByAddress);
    if (!hubIds) return selectedHubs.has(OTHER_HUBS_FILTER_VALUE);
    return hubIds.some((id) => selectedHubs.has(id));
  });
}

/** Antal ben som utgår från en hubb: alla, respektive de som ännu är planerade (typ 4). */
export interface HubAssignmentCount {
  total: number;
  plannedFurtherTransport: number;
}

const emptyCount = (): HubAssignmentCount => ({
  total: 0,
  plannedFurtherTransport: 0,
});

/**
 * Räknar benen per hubbalternativ i dropdownen, med samma adressmatchning som
 * `assignmentFromHubMatches`, så att siffran på ett alternativ är exakt vad man får om man
 * bockar i det. Varje hubb och "Övriga" får alltid en post (0/0 om inget ben utgår därifrån).
 *
 * Ett ben som utgår från en adress två hubbar delar räknas på båda.
 */
export function countAssignmentsByHub(
  assignments: AssignmentViewModel[],
  hubs: CompanyModel[]
): Map<HubFilterValue, HubAssignmentCount> {
  const counts = new Map<HubFilterValue, HubAssignmentCount>();
  counts.set(OTHER_HUBS_FILTER_VALUE, emptyCount());
  hubs.forEach((hub) => counts.set(hub.companyID, emptyCount()));

  const hubIdsByAddress = buildHubIdsByAddress(hubs);

  assignments.forEach(({ assignment }) => {
    const keys: HubFilterValue[] = fromHubIds(assignment, hubIdsByAddress) ?? [
      OTHER_HUBS_FILTER_VALUE,
    ];
    keys.forEach((key) => {
      const count = counts.get(key) ?? emptyCount();
      count.total += 1;
      if (
        assignment.assignmentTypeID ===
        AssignmentTypeEnum.PlannedFurtherTransport
      ) {
        count.plannedFurtherTransport += 1;
      }
      counts.set(key, count);
    });
  });

  return counts;
}

/** Etikettsuffixet i dropdownen: "(59/34)" = 59 ben totalt varav 34 planerade vidaretransporter. */
export const formatHubCount = (count: HubAssignmentCount | undefined) =>
  count ? `(${count.total}/${count.plannedFurtherTransport})` : '';
